import React from 'react';

/**
 * TermsOfServicePage component - Platform terms of service information
 */
const TermsOfServicePage = () => {
  const lastUpdated = 'May 1, 2023';
  
  return (
    <div className="bg-gray-50">
      {/* Header section */}
      <section className="bg-primary-700 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
            Terms of Service
          </h1>
          <p className="text-lg">
            Last Updated: {lastUpdated}
          </p>
        </div>
      </section>
      
      {/* Terms content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none mx-auto">
            <div className="bg-primary-50 border-l-4 border-primary-500 p-4 mb-8 rounded-r">
              <p className="text-lg font-medium text-primary-800 mb-0">
                Welcome to Biotech01. Please read these Terms of Service carefully before using our platform. By accessing or using Biotech01, you agree to be bound by these terms and all applicable laws and regulations.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Acceptance of Terms</h2>
              <p>
                By accessing or using the Biotech01 platform, website, mobile application, and related services (collectively, the "Services"), you agree to be bound by these Terms of Service. If you do not agree to all the terms and conditions of this agreement, you may not access or use the Services.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">User Eligibility</h2>
              <p>
                The Services are intended for users who are at least 18 years old and are either representing themselves or a business entity with the authority to enter into binding agreements. By using the Services, you represent and warrant that you meet these eligibility requirements.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">User Accounts</h2>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Registration and Account Security</h3>
                <p className="mb-3">
                  When you create an account with us, you must provide accurate, complete, and current information. You are responsible for safeguarding your password and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Types</h3>
                <p>
                  Biotech01 offers different account types for startups, investors, and service providers. Each account type may have specific features, requirements, and terms as described during the registration process. We reserve the right to modify account features or requirements at any time.
                </p>
              </div>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Prohibited Activities</h2>
              <p className="mb-3">
                When using our Services, you agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on the intellectual property rights of others</li>
                <li>Upload or transmit viruses, malware, or other malicious code</li>
                <li>Interfere with or disrupt the integrity or performance of the Services</li>
                <li>Attempt to gain unauthorized access to the Services or related systems</li>
                <li>Collect or harvest user data without consent</li>
                <li>Use the Services for any illegal or unauthorized purpose</li>
                <li>Misrepresent your identity or affiliation with any person or entity</li>
                <li>Post false, misleading, or deceptive content</li>
                <li>Engage in any activity that could damage, disable, or impair the functioning of the Services</li>
              </ul>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Intellectual Property Rights</h2>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Our Rights</h3>
                <p className="mb-3">
                  The Biotech01 platform, including its design, text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software, is the property of Biotech01 or its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not use, reproduce, distribute, modify, or create derivative works from any content from our Services without our express written consent.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Content</h3>
                <p>
                  When you upload, submit, or otherwise provide content to our Services ("User Content"), you grant Biotech01 a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, adapt, publish, and display such User Content for the purpose of providing and improving the Services. You represent and warrant that you own or have the necessary rights to grant us this license and that your User Content does not violate the rights of any third party.
                </p>
              </div>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Privacy and Data Protection</h2>
              <p className="mb-3">
                Our collection and use of personal information in connection with the Services is governed by our <a href="/privacy-policy" className="text-primary-600 hover:text-primary-800 font-medium">Privacy Policy</a>. By using the Services, you consent to the collection, processing, and storage of your information as described in the Privacy Policy.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Disclaimer of Warranties</h2>
              <p className="mb-3">
                THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY WARRANTIES OF ANY KIND. BIOTECH01 EXPRESSLY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
              </p>
              <p>
                WE DO NOT WARRANT THAT THE SERVICES WILL MEET YOUR REQUIREMENTS, OPERATE WITHOUT INTERRUPTION, BE ERROR-FREE, OR THAT DEFECTS WILL BE CORRECTED. YOU USE THE SERVICES AT YOUR OWN RISK.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Limitation of Liability</h2>
              <p className="mb-3">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, BIOTECH01 AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your access to or use of or inability to access or use the Services</li>
                <li>Any conduct or content of any third party on the Services</li>
                <li>Any content obtained from the Services</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              </ul>
              <p className="bg-gray-50 p-4 rounded border border-gray-200 mt-4">
                In no event shall our total liability to you for all claims exceed the amount paid by you, if any, for accessing or using our Services during the twelve (12) months preceding the claim.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Biotech01 and its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses (including attorney's fees) arising from: (i) your use of the Services; (ii) your violation of these Terms of Service; (iii) your violation of any third-party right, including without limitation any intellectual property right or privacy right; and (iv) any claim that your User Content caused damage to a third party.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Governing Law</h2>
              <p>
                These Terms of Service shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law principles. Any dispute arising from or relating to these terms or the Services shall be subject to the exclusive jurisdiction of the courts in the United States.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Termination</h2>
              <p>
                We may terminate or suspend your account and access to the Services immediately, without prior notice or liability, for any reason, including if you breach these Terms of Service. Upon termination, your right to use the Services will immediately cease. All provisions of these Terms of Service that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Changes to Terms</h2>
              <p>
                We reserve the right to modify or replace these Terms of Service at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Services after those revisions become effective, you agree to be bound by the revised terms.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="mb-2">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <a href="mailto:legal@biotech01.com" className="text-primary-600 hover:text-primary-800 font-medium">legal@biotech01.com</a>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage; 